import React from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import ContactBlock from "../../components/contact/ContactBlock";

const ProductSelectionGuideScreen = () => {
	// Animation constants matching home page
	const bounceTransition = {
		duration: 0.4,
		ease: [0.68, -0.25, 0.265, 1.25],
	};

	const fabrics = [
		{
			type: "Blackout Roller Shades",
			desc: "Our opaque fabrics let you block out sunlight completely. Our rich collection of fabrics includes a wide variety of colours, textures, and patterns. Ideal for bedrooms, where darkness gives you a restful sleep or theatre rooms for vivid colours on your screen.",
			features: [
				"Complete light blockage",
				"UV protection",
				"Thermal insulation",
				"Eco Friendly",
			],
			image: "/images/selection/blackout.jpg",
		},
		{
			type: "Privacy Roller Shades",
			desc: "Our extensive collection of privacy fabrics has it all: offering patterns, solids, and textures, allowing you to find the perfect fabric to complement your decor and provide privacy while considerably blocking the sun.",
			features: [
				"Complete privacy",
				"Partial light filtering",
				"PVC free",
				"Contemporary designs",
			],
			image: "/images/selection/privacy.jpg",
		},
		{
			type: "Sunscreen Roller Shades",
			desc: "Sunscreen fabrics are performance fabrics which give the ultimate control over the natural light. Our broad range of sunscreens make them appropriate for any application.",
			features: ["Solar heat reduction", "UV protection", "Glare control"],
			image: "/images/selection/solar.jpg",
		},
		{
			type: "Transparent Roller Shades",
			desc: "Our diverse transparent fabric collection gives you a multitude of decorative options which allow full outward visibility and a softened glow of natural light.",
			features: [
				"Full outward visibility",
				"Natural light flow",
				"Northern exposure ideal",
			],
			image: "/images/selection/transparent.jpg",
		},
		{
			type: "Translucent Roller Shades",
			desc: "Our translucent fabrics all have a unique element of interest, from a subtle sheen to pronounced textures to multi-tone. They provide some privacy and a warm, intimate glow to your room with diffused sunlight.",
			features: [
				"Partial privacy",
				"Diffused sunlight",
				"UV protection",
				"Eco-friendly",
				"PVC free",
			],
			image: "/images/selection/translucent.jpg",
		},
	];

	return (
		<div className="overflow-x-hidden bg-gradient-to-b from-white to-gray-50">
			<Helmet>
				<title>
					Find Your Perfect Level of Shade Opacity | Roller Shades Guide | Off
					Cut Shades
				</title>
				<meta
					name="description"
					content="Not sure what level of opacity to choose for your roller shades? Our guide will help you find the perfect level of shade for your space."
				/>
				<link
					rel="canonical"
					href="https://offcutshades.com/help/productselection"
				/>
			</Helmet>

			<main className="container mx-auto px-4 py-12">
				{/* Hero Section */}
				<motion.div
					className="text-center mb-16"
					initial={{ opacity: 0, y: 30 }}
					animate={{ opacity: 1, y: 0 }}
					transition={bounceTransition}
				>
					<div className="flex justify-center mb-6">
						<motion.img
							src="/images/newHome/fabric-selection-logo-80x80.png"
							alt="Fabric Selection"
							className="w-20 h-20"
							initial={{ scale: 0 }}
							animate={{ scale: 1 }}
							transition={{ ...bounceTransition, delay: 0.2 }}
						/>
					</div>

					<motion.h1
						className="relative z-10 text-5xl md:text-7xl font-bold mb-6 leading-tight"
						initial={{ opacity: 0, y: 30 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ ...bounceTransition, delay: 0.3 }}
					>
						Find the Perfect Fabric for Your{" "}
						<span className="relative">
							<span className="text-primary">Shade</span>
						</span>
					</motion.h1>

					<motion.p
						className="text-xl text-gray-600 max-w-3xl mx-auto"
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ ...bounceTransition, delay: 0.4 }}
					>
						Discover our extensive collection of window treatments designed to
						perfectly match your needs, from complete darkness to gentle light
						filtering.
					</motion.p>
				</motion.div>

				{/* Showcase Image */}
				<motion.div
					className="relative mb-16 rounded-3xl overflow-hidden shadow-2xl aspect-[21/9]"
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={bounceTransition}
				>
					<img
						src="/images/windows/filler/1.jpg"
						alt="Window Treatments Showcase"
						className="w-full h-full object-cover"
					/>
					<div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent pointer-events-none" />
				</motion.div>

				{/* Fabric Showcase */}
				<div className="space-y-24">
					<h2 className="text-4xl font-bold mb-12 text-center">
						Roller Shade Features
					</h2>
					{fabrics.map((fabric, index) => (
						<motion.div
							key={index}
							className="grid md:grid-cols-2 gap-12 items-center"
							initial={{ opacity: 0, y: 50 }}
							whileInView={{ opacity: 1, y: 0 }}
							transition={{ ...bounceTransition, delay: index * 0.1 }}
							viewport={{ once: true }}
						>
							<motion.div
								className={`relative ${
									index % 2 === 0 ? "md:order-1" : "md:order-2"
								}`}
								whileHover={{ scale: 1.02 }}
								transition={{ duration: 0.2 }}
							>
								<div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl">
									<img
										src={fabric.image}
										alt={fabric.type}
										className="w-full h-full object-cover"
									/>
									<div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent pointer-events-none" />
								</div>
							</motion.div>

							<div className={index % 2 === 0 ? "md:order-2" : "md:order-1"}>
								<h2 className="text-4xl font-bold mb-6">{fabric.type}</h2>
								<p className="text-gray-600 text-lg mb-8">{fabric.desc}</p>

								<div className="space-y-4">
									{fabric.features.map((feature, featureIndex) => (
										<motion.div
											key={featureIndex}
											className="flex items-center gap-4"
											initial={{ opacity: 0, x: -20 }}
											whileInView={{ opacity: 1, x: 0 }}
											transition={{
												...bounceTransition,
												delay: 0.2 + featureIndex * 0.1,
											}}
										>
											<div className="w-2 h-2 rounded-full bg-primary" />
											<span className="text-gray-700">{feature}</span>
										</motion.div>
									))}
								</div>
							</div>
						</motion.div>
					))}
				</div>
			</main>
			{/* Contact Section */}
			<ContactBlock />
		</div>
	);
};

export default ProductSelectionGuideScreen;
